/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/app/App.tsx
 * layer: web
 * area: App.tsx
 * kind: source
 * web_root: App.tsx
 * relpath: web/src/app/App.tsx
 * filename: App.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate } from 'react-router-dom';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  reportUnhandledMutationError,
  reportUnhandledQueryError,
} from '@/platform/api-client/unhandledErrorFeedback';
import { Toaster } from 'sonner';
import { ThemeProvider } from '@/platform/design-system/providers/ThemeProvider';
import { AuthProvider } from '@/platform/auth/AuthProvider';
import { TenantProvider } from '@/platform/tenancy/TenantProvider';
import { PostHogProvider } from '@/platform/observability/PostHogProvider';
import { useAuth } from '@/platform/authentication/hooks/useAuth';
import { ProtectedRoute } from '@/platform/authentication/components/ProtectedRoute';
import { ErrorBoundary } from '@/app/composition/components/ErrorBoundary';
import { LoadingState } from '@/platform/design-system/components/ui/LoadingState';
import { SiteAssessmentNotificationHandler } from '@/capabilities/device-management/components/SiteAssessmentNotificationHandler';
import { BrowserNotificationDispatcher } from '@/platform/realtime/components/BrowserNotificationDispatcher';
import { P1InterruptionNavigator } from '@/capabilities/service-operations/components/engineer/P1InterruptionNavigator';
import { workbenchRoleProfile } from '@/app/navigation/routes/workbenchRoles';
import { RouteNotFoundBoundary } from '@/app/navigation/routes/RouteNotFoundBoundary';
import { WorkbenchRoutes } from '@/app/navigation/routes/destinations/WorkbenchRoutes';
import { RunwayRoutes } from '@/app/navigation/routes/destinations/RunwayRoutes';
import { PortfolioRoutes } from '@/app/navigation/routes/destinations/PortfolioRoutes';
import { LibraryRoutes } from '@/app/navigation/routes/destinations/LibraryRoutes';
import { SwitchboardRoutes } from '@/app/navigation/routes/destinations/SwitchboardRoutes';
import { DESIGN_ROUTE_ELEMENTS } from '@/app/navigation/routes/DesignRouteElements';
import { PLATFORM_ROUTE_ELEMENT } from '@/app/navigation/routes/PlatformRouteElement';
import { CUSTOMER_PORTAL_ROUTE_ELEMENTS } from '@/app/portal/routes';

// Authenticated shells carry navigation, field-guide, notification and
// workflow infrastructure that public auth routes do not need. Keep that
// operational UI behind the protected-route boundary instead of placing it in
// every visitor's initial module graph.
const AppShell = lazy(() =>
  import('@/app/composition/components/AppShell').then(({ AppShell }) => ({ default: AppShell }))
);

// Route owners publish lazy page elements from their own route packages.
import { ProposalView } from '@/capabilities/customer-lifecycle/public/routes/lazyPages';
import { ConfirmActionHost } from '@/platform/design-system/components/shared/confirmAction';
import { RemoteSession } from '@/capabilities/device-management/public/routes/lazyPages';
import {
  FirstTimeApp,
  LicenseSuspended,
  OAuthCallback,
  PinSetupPage,
  RecoveryApp,
  SigninApp,
} from '@/platform/authentication/routes/lazyPages';

const queryClient = new QueryClient({
  // Failures that no component handles get a toast instead of vanishing or
  // rendering as a false empty state (see unhandledErrorFeedback.ts).
  queryCache: new QueryCache({
    onError: (error, query) => reportUnhandledQueryError(error, query),
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _onMutateResult, mutation) =>
      reportUnhandledMutationError(error, mutation),
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function RoleLandingRedirect() {
  const { user } = useAuth();
  const target = workbenchRoleProfile(user).landing;
  return <Navigate to={target} replace />;
}

/**
 * The route tree. Mounted as the single splat element of a data router
 * (`createBrowserRouter`) so that `useBlocker` works for unsaved-changes guards
 * (see useUnsavedChanges). The JSX route tree itself is unchanged: descendant
 * `<Routes>` resolve exactly as they did under `<BrowserRouter>`.
 */
function AppRoutes() {
  return (
    <AuthProvider>
      <TenantProvider>
        <PostHogProvider>
          <ErrorBoundary>
            <Suspense
              fallback={
                <LoadingState
                  variant="skeleton-dashboard"
                  label="Loading Hyperion"
                  description="Preparing the requested surface."
                  className="min-h-screen p-6"
                />
              }
            >
              <Routes>
                {/* Public routes — auth-v2 (legacy LoginPage / MFAPage /
              RecoveryPage deleted in step 23 of the auth-perfection
              plan; rollback is via git history). */}
                <Route path="/login" element={<SigninApp />} />
                <Route path="/recovery" element={<RecoveryApp />} />
                <Route path="/first-time" element={<FirstTimeApp />} />
                <Route path="/auth/callback" element={<OAuthCallback />} />
                <Route path="/suspended" element={<LicenseSuspended />} />
                {CUSTOMER_PORTAL_ROUTE_ELEMENTS}

                {/* PIN setup runs post-MFA, pre-app — kept as its own page. */}
                <Route path="/mfa/pin-setup" element={<PinSetupPage />} />

                {/* Standalone proposal view — no Shell, clean document preview */}
                <Route
                  path="/proposals/:proposalId/view"
                  element={
                    <ProtectedRoute>
                      <ProposalView />
                    </ProtectedRoute>
                  }
                />

                {/* Standalone remote session — no Shell, opens in popup window */}
                <Route
                  path="/remote-session/:id"
                  element={
                    <ProtectedRoute>
                      <RemoteSession />
                    </ProtectedRoute>
                  }
                />

                {PLATFORM_ROUTE_ELEMENT}

                {/* Protected routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <ErrorBoundary>
                        <SiteAssessmentNotificationHandler />
                        <BrowserNotificationDispatcher />
                        <P1InterruptionNavigator />
                        <Suspense
                          fallback={
                            <LoadingState
                              variant="skeleton-dashboard"
                              label="Loading destination"
                              description="Preparing the requested Hyperion surface."
                              className="min-h-screen p-6"
                            />
                          }
                        >
                          <AppShell />
                        </Suspense>
                      </ErrorBoundary>
                    </ProtectedRoute>
                  }
                >
                  {/* Role-based landing route. */}
                  <Route index element={<RoleLandingRedirect />} />

                  {/* Five destination-owned routers are the only product roots. */}
                  <Route path="workbench/*" element={<WorkbenchRoutes />} />
                  <Route path="runway/*" element={<RunwayRoutes />} />
                  <Route path="portfolio/*" element={<PortfolioRoutes />} />
                  <Route path="library/*" element={<LibraryRoutes />} />
                  <Route path="switchboard/*" element={<SwitchboardRoutes />} />

                  {DESIGN_ROUTE_ELEMENTS}

                  {/* Legacy functions are hosted by the five destination routers. */}

                  {/* Retired and mistyped paths fail visibly; no legacy pointer routes. */}
                  <Route path="*" element={<RouteNotFoundBoundary />} />
                </Route>
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </PostHogProvider>
      </TenantProvider>
    </AuthProvider>
  );
}

const appRouter = createBrowserRouter([{ path: '*', element: <AppRoutes /> }]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ConfirmActionHost />
        <Toaster
          position="bottom-right"
          closeButton
          theme="system"
          toastOptions={{
            // Ensure readable contrast in both themes by setting explicit
            // bg + foreground per type. Sonner's `richColors` mode tints
            // bg AND text to the same colour family which produces
            // unreadable green-on-green / red-on-red in dark mode.
            classNames: {
              toast:
                'rounded-xl border bg-[rgb(var(--color-bg-secondary))] text-[rgb(var(--color-text-primary))] border-[rgb(var(--color-border-primary)/0.6)] shadow-lg',
              title: 'text-[rgb(var(--color-text-primary))] font-semibold',
              description: 'text-[rgb(var(--color-text-secondary))]',
              actionButton:
                'bg-accent text-accent-text hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-accent-ring',
              cancelButton:
                'bg-[rgb(var(--color-bg-tertiary))] text-[rgb(var(--color-text-secondary))] hover:bg-[rgb(var(--color-bg-tertiary)/0.8)]',
              closeButton:
                'bg-[rgb(var(--color-bg-tertiary))] text-[rgb(var(--color-text-secondary))] border-[rgb(var(--color-border-primary)/0.4)]',
              success:
                '!border-l-4 !border-l-green-500 !bg-[rgb(var(--color-bg-secondary))] !text-[rgb(var(--color-text-primary))]',
              error:
                '!border-l-4 !border-l-red-500 !bg-[rgb(var(--color-bg-secondary))] !text-[rgb(var(--color-text-primary))]',
              warning:
                '!border-l-4 !border-l-amber-500 !bg-[rgb(var(--color-bg-secondary))] !text-[rgb(var(--color-text-primary))]',
              info: '!border-l-4 !border-l-blue-500 !bg-[rgb(var(--color-bg-secondary))] !text-[rgb(var(--color-text-primary))]',
            },
          }}
        />
        <RouterProvider router={appRouter} />
      </ThemeProvider>
    </QueryClientProvider>
  );
}
